import {
  Alert,
  AlertDescription,
  Button,
  ColumnHint,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  LoadingState,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';


import { createOrg, listOrgs, viewAsOrg } from '../../api/admin.js';
import { ApiError } from '../../lib/api-client.js';

export function AdminOrgsPage() {
  const queryClient = useQueryClient();
  const orgsQuery = useQuery({ queryKey: ['admin-orgs'], queryFn: listOrgs });
  const [createOpen, setCreateOpen] = useState(false);

  const createMutation = useMutation({
    mutationFn: createOrg,
    onSuccess: (data) => {
      toast.success(`Created ${data.org.name}`);
      setCreateOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] });
    },
  });

  const viewAsMutation = useMutation({
    mutationFn: viewAsOrg,
    onSuccess: (data) => {
      // Phase 0 dev: switch the local subdomain override and redirect to /handoff
      // with the token in the URL hash. In production the redirect would be to
      // `https://<slug>.sitewise.app/handoff#handoff=<token>`.
      const isLocalDev = window.location.hostname.endsWith('localhost') || window.location.hostname === 'localhost';
      if (isLocalDev) {
        window.localStorage.setItem('sitewise:subdomain-override', data.orgSlug);
        window.location.assign(`/handoff#handoff=${encodeURIComponent(data.handoffToken)}`);
      } else {
        const proto = window.location.protocol;
        window.location.assign(
          `${proto}//${data.orgSlug}.${window.location.host.split('.').slice(-2).join('.')}/handoff#handoff=${encodeURIComponent(data.handoffToken)}`,
        );
      }
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not start view-as session');
    },
  });

  return (
    <>
      <PageHeader
        title="Organizations"
        description="Every tenant in SiteWise. Create new orgs and view-as to assist users."
        action={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            New organization
          </Button>
        }
      />

      {orgsQuery.isPending ? (
        <LoadingState rows={5} />
      ) : orgsQuery.isError ? (
        <Alert variant="danger">
          <AlertDescription>Could not load organizations.</AlertDescription>
        </Alert>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <ColumnHint description="The organization name.">Name</ColumnHint>
                </TableHead>
                <TableHead>
                  <ColumnHint description="The short id used in the organization's address.">Slug</ColumnHint>
                </TableHead>
                <TableHead>
                  <ColumnHint description="How many people belong to the organization.">Users</ColumnHint>
                </TableHead>
                <TableHead>
                  <ColumnHint description="When the organization was added.">Created</ColumnHint>
                </TableHead>
                <TableHead className="text-right">
                  <ColumnHint description="Open or manage the organization.">Actions</ColumnHint>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orgsQuery.data.orgs.map((org) => (
                <TableRow key={org.id}>
                  <TableCell>
                    <Link to={`/admin/orgs/${org.id}`} className="font-medium hover:underline">
                      {org.name}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-fg-muted">{org.slug}</TableCell>
                  <TableCell className="tabular-nums">{org.userCount ?? 0}</TableCell>
                  <TableCell className="text-fg-muted text-xs">
                    {new Date(org.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => viewAsMutation.mutate(org.id)}
                      disabled={viewAsMutation.isPending}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      View as
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateOrgDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={(input) => createMutation.mutate(input)}
        pending={createMutation.isPending}
        error={createMutation.error}
      />
    </>
  );
}

interface CreateOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: {
    slug: string;
    name: string;
    initialAdminEmail: string;
    initialAdminName: string;
  }) => void;
  pending: boolean;
  error: unknown;
}

function CreateOrgDialog(props: CreateOrgDialogProps) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>
            The initial admin will receive an invitation email at the address below.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            props.onSubmit({ slug, name, initialAdminEmail: adminEmail, initialAdminName: adminName });
          }}
          noValidate
        >
          <div className="space-y-1.5">
            <Label htmlFor="org-name-create">Display name</Label>
            <Input
              id="org-name-create"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Property Management"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="org-slug-create">Subdomain slug</Label>
            <div className="flex items-center gap-2">
              <Input
                id="org-slug-create"
                required
                pattern="^[a-z0-9-]+$"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                placeholder="acme"
                className="font-mono"
              />
              <span className="text-sm text-fg-muted">.sitewise.app</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="admin-name-create">Initial admin name</Label>
            <Input
              id="admin-name-create"
              required
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="admin-email-create">Initial admin email</Label>
            <Input
              id="admin-email-create"
              type="email"
              required
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
            />
          </div>
          {props.error ? (
            <Alert variant="danger">
              <AlertDescription>
                {props.error instanceof ApiError ? props.error.message : 'Could not create organization.'}
              </AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => props.onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={props.pending}>
              {props.pending ? 'Creating…' : 'Create organization'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
